import { Toast } from '@/components/Toast';
import { NaZakazkuTrideniList } from '@/components/vyroba/NaZakazkuTrideniList';
import { TerminyPrehled } from '@/components/vyroba/TerminyPrehled';
import type { AvailabilitySync, ProductionTerm, TermEshopSync } from '@/components/vyroba/types';
import { useEscapeKey } from '@/hooks/useEscapeKey';
import { ApiError, apiFetch } from '@/lib/apiFetch';
import { type FormEvent, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

type TerminAuditRow = {
  sku: string;
  product_name: string;
  required: number;
  real_qty: number;
  planned: number;
  covered: number;
  shortfall: number;
  orders: { eshop: string; code: string; status: string; qty: number }[];
};

type TerminAuditResult = {
  term_date: string;
  checked_orders: number;
  skus_checked: number;
  problems: TerminAuditRow[];
  errors: string[];
};

/**
 * Termíny výroby — číselník dat, do kterých se zařazují produkty.
 * Proces je: nejdřív se vypíše termín, teprve pak se do něj přidávají kusy.
 */
/**
 * Nejbližší termín nahoře: budoucí vzestupně (co je za dveřmi, je první),
 * pod nimi minulé sestupně — historie zůstává po ruce, ale nepřekáží.
 */
function sortTerms(terms: ProductionTerm[]): ProductionTerm[] {
  const today = new Date().toISOString().slice(0, 10);
  const future = terms
    .filter((t) => t.term_date >= today)
    .sort((a, b) => a.term_date.localeCompare(b.term_date));
  const past = terms
    .filter((t) => t.term_date < today)
    .sort((a, b) => b.term_date.localeCompare(a.term_date));
  return [...future, ...past];
}

const TABS = [
  { id: 'terminy', label: 'Termíny' },
  { id: 'prehled', label: 'Přehled dokončenosti' },
  { id: 'trideni', label: 'Třídění na zakázku' },
] as const;
type Tab = (typeof TABS)[number]['id'];

export function VyrobaTerminyPage() {
  const [tab, setTab] = useState<Tab>('terminy');
  const [terms, setTerms] = useState<ProductionTerm[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [toast, setToast] = useState<{ msg: string; kind: 'success' | 'error' } | null>(null);
  const [auditTerm, setAuditTerm] = useState<ProductionTerm | null>(null);
  const [auditResult, setAuditResult] = useState<TerminAuditResult | null>(null);
  const [auditBusy, setAuditBusy] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setError(null);
    try {
      const data = await apiFetch<{ terms: ProductionTerm[] }>('/api/vyroba/terms');
      setTerms(sortTerms(data.terms));
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.message : 'Chyba načítání');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /** Shrne výsledek synchronizace do jedné věty pro toast. */
  function describeSync(
    sync: AvailabilitySync[] | undefined,
    isService?: boolean,
  ): {
    msg: string;
    kind: 'success' | 'error';
  } {
    if (isService) {
      return { msg: 'Servisní termín přidán — bez propsání do e-shopu', kind: 'success' };
    }
    if (!sync || sync.length === 0) {
      return { msg: 'Termín přidán (žádný aktivní e-shop k napojení)', kind: 'success' };
    }
    // Selhání dostupnosti je chyba; „missing" u stavu objednávky je to-do.
    const failed = sync.filter((s) => s.status === 'failed');
    if (failed.length > 0) {
      const first = failed[0];
      const what = first?.kind === 'order_status' ? 'stav objednávky' : 'dostupnost';
      return {
        msg: `Termín přidán, ale ${what}: ${first?.error_message ?? 'neznámá chyba'}`,
        kind: 'error',
      };
    }
    const missing = sync.some((s) => s.status === 'missing');
    if (missing) {
      return {
        msg: 'Termín přidán a dostupnost založena. Stav objednávky vytvoř ručně v e-shopu — viz sloupec Dostupnost v e-shopu.',
        kind: 'success',
      };
    }
    return { msg: 'Termín přidán, vše propojeno s e-shopem', kind: 'success' };
  }

  async function retrySync(t: ProductionTerm) {
    try {
      const res = await apiFetch<{ sync: AvailabilitySync[] }>(
        `/api/vyroba/terms/${t.id}/sync-availability`,
        { method: 'POST' },
      );
      setToast(describeSync(res.sync));
      await load();
    } catch (e: unknown) {
      setToast({ msg: e instanceof ApiError ? e.message : 'Synchronizace selhala', kind: 'error' });
    }
  }

  async function remove(t: ProductionTerm) {
    if (!confirm(`Opravdu smazat termín ${fmtCzDate(t.term_date)}?`)) return;
    try {
      await apiFetch(`/api/vyroba/terms/${t.id}`, { method: 'DELETE' });
      setToast({ msg: 'Termín smazán', kind: 'success' });
      await load();
    } catch (e: unknown) {
      setToast({ msg: e instanceof ApiError ? e.message : 'Smazání selhalo', kind: 'error' });
    }
  }

  async function runAudit(t: ProductionTerm) {
    setAuditTerm(t);
    setAuditResult(null);
    setAuditBusy(true);
    try {
      const d = await apiFetch<TerminAuditResult>(`/api/vyroba/terms/${t.id}/audit`, {
        method: 'POST',
      });
      setAuditResult(d);
    } catch (e: unknown) {
      setToast({ msg: e instanceof ApiError ? e.describe() : 'Audit selhal', kind: 'error' });
      setAuditTerm(null);
    } finally {
      setAuditBusy(false);
    }
  }

  const today = new Date().toISOString().slice(0, 10);

  return (
    <>
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-text">Termíny výroby</h1>
          <p className="text-sm text-text-secondary mt-0.5 max-w-2xl">
            Data, ke kterým se plánuje výroba. Do každého termínu se pak zařazují konkrétní produkty
            a velikosti.
          </p>
        </div>
        {tab !== 'trideni' && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="bg-action hover:bg-action-dark text-white text-sm font-semibold rounded-lg px-6 py-2.5 transition-colors cursor-pointer"
          >
            Přidat termín výroby
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-5">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`rounded-lg px-5 py-3 text-[15px] font-bold transition-colors cursor-pointer ${
              tab === t.id
                ? 'bg-action text-white'
                : 'bg-bg border border-border text-text-secondary hover:border-text-muted'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="text-sm text-danger bg-danger-light border border-danger/30 rounded-lg px-4 py-3 mb-4">
          {error}
        </div>
      )}

      {tab === 'prehled' && <TerminyPrehled terms={terms} onRefresh={load} />}

      {tab === 'trideni' && <NaZakazkuTrideniList />}

      {tab === 'terminy' && (
        <div className="bg-surface rounded-xl shadow-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-bg">
                <Th>Datum</Th>
                <Th right>Počet produktů</Th>
                <Th right>Kusů celkem</Th>
                <Th>Dostupnost v e-shopu</Th>
                <Th>{''}</Th>
              </tr>
            </thead>
            <tbody>
              {terms == null ? (
                <tr>
                  <td colSpan={5} className="px-5 py-8 text-center text-text-muted">
                    Načítám…
                  </td>
                </tr>
              ) : terms.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-5 py-8 text-center text-text-muted">
                    Zatím žádné termíny. Klikni „Přidat termín výroby".
                  </td>
                </tr>
              ) : (
                terms.map((t) => (
                  <tr
                    key={t.id}
                    className="border-b border-border-light hover:bg-surface-hover transition-colors"
                  >
                    <td className="px-5 py-3">
                      <button
                        type="button"
                        onClick={() => navigate(`/vyroba?term=${t.id}`)}
                        className="font-semibold text-text hover:text-primary transition-colors cursor-pointer"
                      >
                        {fmtCzDate(t.term_date)}
                      </button>
                      {!!t.is_service && (
                        <span className="ml-2 text-[11px] font-semibold text-primary bg-primary-50 px-1.5 py-0.5 rounded">
                          servisní
                        </span>
                      )}
                      {t.term_date < today && (
                        <span className="ml-2 text-[11px] text-text-muted">proběhlý</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-right text-text font-semibold">
                      {t.products_count}
                    </td>
                    <td className="px-5 py-3 text-right text-text-secondary">{t.planned_qty}</td>
                    <td className="px-5 py-3">
                      {t.is_service ? (
                        <span className="text-[11px] text-text-muted">
                          Servisní termín — bez propsání do e-shopu
                        </span>
                      ) : t.eshops.length === 0 ? (
                        <button
                          type="button"
                          onClick={() => retrySync(t)}
                          className="text-[11px] font-semibold text-primary hover:underline cursor-pointer"
                        >
                          Propojit s e-shopem
                        </button>
                      ) : (
                        <div className="space-y-2.5">
                          {t.eshops.map((e) => (
                            <EshopSyncBlock key={e.eshop_code} e={e} onCheck={() => retrySync(t)} />
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-3 text-right whitespace-nowrap">
                      {!t.is_service && (
                        <button
                          type="button"
                          onClick={() => runAudit(t)}
                          className="text-sm font-semibold text-primary hover:underline cursor-pointer mr-4"
                        >
                          Zkontrolovat pokrytí
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => remove(t)}
                        className="text-sm font-semibold text-danger hover:underline cursor-pointer"
                      >
                        Smazat
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {adding && (
        <AddTermModal
          onClose={() => setAdding(false)}
          onSaved={(sync, isService) => {
            setAdding(false);
            setToast(describeSync(sync, isService));
            load();
          }}
        />
      )}

      {auditTerm && (
        <TerminAuditModal
          term={auditTerm}
          result={auditResult}
          busy={auditBusy}
          onClose={() => setAuditTerm(null)}
        />
      )}

      {toast && <Toast message={toast.msg} kind={toast.kind} onClose={() => setToast(null)} />}
    </>
  );
}

/** Výsledek "Zkontrolovat pokrytí" — kryje termín skladovost + naplánovaná výroba? */
function TerminAuditModal({
  term,
  result,
  busy,
  onClose,
}: {
  term: ProductionTerm;
  result: TerminAuditResult | null;
  busy: boolean;
  onClose: () => void;
}) {
  useEscapeKey(onClose);
  // Ruční odškrtávání vyřešených SKU — jen vizuální pomůcka, nic se neukládá.
  const [resolved, setResolved] = useState<Set<string>>(new Set());
  const toggleResolved = (sku: string) =>
    setResolved((prev) => {
      const next = new Set(prev);
      if (next.has(sku)) next.delete(sku);
      else next.add(sku);
      return next;
    });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        onClick={onClose}
        aria-label="Zavřít"
        className="absolute inset-0 bg-black/30 backdrop-blur-sm cursor-default"
      />
      <div className="relative w-full max-w-3xl max-h-[85vh] overflow-y-auto bg-surface rounded-xl p-6 shadow-modal reveal">
        <h2 className="text-lg font-bold text-text mb-1">
          Pokrytí termínu {fmtCzDate(term.term_date)}
        </h2>
        <p className="text-sm text-text-secondary mb-4">
          Objednávky ve „Vyřizuje se" (čerpají ze skladu už teď) + objednávky zařazené do tohoto
          termínu i všech starších nevypuštěných termínů, proti reálné skladovosti v Brani plus
          naplánované výrobě splatné do tohoto data. Klikni na řádek, až ho vyřešíš.
        </p>

        {busy || !result ? (
          <p className="text-sm text-text-muted">Kontroluji…</p>
        ) : (
          <>
            <div className="text-sm text-text-secondary mb-2">
              Zkontrolováno {result.checked_orders} objednávek, {result.skus_checked} různých SKU.
            </div>
            {result.errors.length > 0 && (
              <ul className="mb-3 space-y-0.5 text-[13px] text-danger">
                {result.errors.map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            )}
            {result.problems.length === 0 ? (
              <div className="text-sm font-semibold text-success">
                ✓ Termín je pokrytý — na žádné SKU nechybí kusy.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-bg text-left text-xs font-semibold text-text-secondary">
                      <th className="px-3 py-2">SKU</th>
                      <th className="px-3 py-2">Produkt</th>
                      <th className="px-3 py-2 text-right">Potřeba</th>
                      <th className="px-3 py-2 text-right">Sklad</th>
                      <th className="px-3 py-2 text-right">Výroba</th>
                      <th className="px-3 py-2 text-right">Chybí</th>
                      <th className="px-3 py-2">Objednávky</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.problems.map((p) => (
                      // biome-ignore lint/a11y/useKeyWithClickEvents: řádek tabulky, klik jen odškrtne vyřešené (interní nástroj)
                      <tr
                        key={p.sku}
                        onClick={() => toggleResolved(p.sku)}
                        className={`border-b border-border-light align-top cursor-pointer transition-colors ${
                          resolved.has(p.sku)
                            ? 'bg-success-light hover:bg-success-light'
                            : 'hover:bg-surface-hover'
                        }`}
                      >
                        <td className="px-3 py-1.5 font-mono text-text whitespace-nowrap">
                          {p.sku}
                        </td>
                        <td className="px-3 py-1.5 text-text-secondary">{p.product_name}</td>
                        <td className="px-3 py-1.5 text-right text-text">{p.required}</td>
                        <td className="px-3 py-1.5 text-right text-text-secondary">{p.real_qty}</td>
                        <td className="px-3 py-1.5 text-right text-text-secondary">{p.planned}</td>
                        <td className="px-3 py-1.5 text-right font-bold text-danger">
                          {p.shortfall}
                        </td>
                        <td className="px-3 py-1.5 text-[13px] text-text-secondary">
                          {p.orders
                            .map((o) => `${o.code} (${o.eshop}, ${o.status}, ${o.qty} ks)`)
                            .join(' · ')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        <div className="flex justify-end mt-5">
          <button
            type="button"
            onClick={onClose}
            className="text-sm font-semibold text-text-secondary hover:text-text px-4 py-2 cursor-pointer"
          >
            Zavřít
          </button>
        </div>
      </div>
    </div>
  );
}

function AddTermModal({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: (sync: AvailabilitySync[] | undefined, isService: boolean) => void;
}) {
  const [date, setDate] = useState('');
  const [isService, setIsService] = useState(false);
  const [saving, setSaving] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setLocalError(null);
    if (!date) {
      setLocalError('Vyber datum');
      return;
    }
    setSaving(true);
    try {
      const res = await apiFetch<{ sync?: AvailabilitySync[] }>('/api/vyroba/terms', {
        method: 'POST',
        body: { term_date: date, is_service: isService },
      });
      onSaved(res.sync, isService);
    } catch (e: unknown) {
      setLocalError(e instanceof ApiError ? e.describe() : 'Uložení selhalo');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        onClick={onClose}
        aria-label="Zavřít"
        className="absolute inset-0 bg-black/30 backdrop-blur-sm cursor-default"
      />
      <div className="relative w-full max-w-sm bg-surface rounded-xl p-6 shadow-modal reveal">
        <h2 className="text-lg font-bold text-text mb-5 text-center">Přidat termín výroby</h2>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label htmlFor="term-date" className="block text-sm font-medium text-text mb-1.5">
              Datum
            </label>
            <input
              id="term-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>

          <label className="flex items-start gap-2.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={isService}
              onChange={(e) => setIsService(e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded border-border cursor-pointer accent-primary"
            />
            <span className="text-sm text-text">
              <span className="block font-medium">Servisní termín</span>
              <span className="block text-text-secondary text-xs mt-0.5">
                Pro skladové opravy (fyzicky chybějící kus) — zařadí se do výroby normálně, ale
                nepropíše se do e-shopu (žádná dostupnost, žádný stav objednávky).
              </span>
            </span>
          </label>

          {localError && <div className="text-sm text-danger">{localError}</div>}

          <div className="flex justify-center gap-3 pt-1">
            <button
              type="submit"
              disabled={saving}
              className="bg-action hover:bg-action-dark disabled:opacity-50 text-white text-sm font-semibold rounded-lg px-6 py-2 transition-colors cursor-pointer"
            >
              {saving ? 'Ukládám…' : 'Uložit'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="text-sm font-semibold text-text-secondary hover:text-text px-4 py-2 cursor-pointer"
            >
              Zrušit
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/** Vlaječka podle ISO kódu země. Neznámá země → neutrální praporek. */
const COUNTRY_FLAG: Record<string, string> = { CZ: '🇨🇿', SK: '🇸🇰' };

/**
 * Stav propsání termínu do jednoho e-shopu: vlaječka + název e-shopu a pod
 * ním stav dostupnosti a stavu objednávky. Se dvěma e-shopy (CZ, SK) je pak
 * na první pohled vidět, kde je co hotové.
 */
function EshopSyncBlock({ e, onCheck }: { e: TermEshopSync; onCheck: () => void }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-0.5">
        <span aria-hidden>{COUNTRY_FLAG[e.country] ?? '🏳️'}</span>
        <span className="text-xs font-semibold text-text">{e.eshop_name}</span>
      </div>
      <div className="pl-5 space-y-0.5">
        <Pill
          label="Dostupnost"
          status={e.availability?.status ?? null}
          error={e.availability?.error_message ?? null}
        />
        <Pill
          label="Stav objednávky"
          status={e.order_status?.status ?? null}
          error={e.order_status?.error_message ?? null}
          missingName={e.order_status?.remote_name ?? null}
          missingNote="Vytvoř ručně stav objednávky i e-mailové oznámení navázané na tento stav."
        />
      </div>
      <button
        type="button"
        onClick={onCheck}
        className="text-[11px] font-semibold text-primary hover:underline cursor-pointer pl-5 mt-0.5"
      >
        Zkontrolovat
      </button>
    </div>
  );
}

/**
 * Jeden řádek stavu (dostupnost / stav objednávky). „missing" = existuje jen
 * u stavu objednávky (Shoptet ho neumí založit přes API) a ukáže název
 * k ručnímu vytvoření.
 */
function Pill({
  label,
  status,
  error,
  missingName,
  missingNote,
}: {
  label: string;
  status: 'created' | 'exists' | 'missing' | 'failed' | null;
  error: string | null;
  missingName?: string | null;
  /** Doplňková připomínka pod „vytvoř ručně" (např. že se má vytvořit i e-mail). */
  missingNote?: string | null;
}) {
  if (status === 'created' || status === 'exists') {
    return (
      <div className="text-[11px] text-text-secondary">
        <span className="font-semibold text-success">✓</span> {label}: v e-shopu
      </div>
    );
  }
  if (status === 'missing') {
    return (
      <div className="text-[11px] text-text-secondary">
        <span className="font-semibold text-warning">!</span> {label}: vytvoř ručně
        {missingName && <span className="block pl-3 font-mono text-text">„{missingName}"</span>}
        {missingNote && <span className="block pl-3 text-text-muted">{missingNote}</span>}
      </div>
    );
  }
  if (status === 'failed') {
    return (
      <div className="text-[11px] text-text-secondary">
        <span className="font-semibold text-danger">✕</span> {label}: chyba
        {error && <span className="block pl-3 text-text-muted">{error}</span>}
      </div>
    );
  }
  return (
    <div className="text-[11px] text-text-muted">
      <span>·</span> {label}: nezkontrolováno
    </div>
  );
}

/** 'YYYY-MM-DD' → 'DD.MM.YYYY' (formát, na který jsou lidi ve výrobě zvyklí). */
function fmtCzDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  return y && m && d ? `${d}.${m}.${y}` : iso;
}

function Th({ children, right }: { children: React.ReactNode; right?: boolean }) {
  return (
    <th
      className={`px-5 py-3 text-[11px] font-semibold text-text-muted uppercase tracking-wider ${
        right ? 'text-right' : 'text-left'
      }`}
    >
      {children}
    </th>
  );
}
